import { CSSProperties, useMemo, useState } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    ToggleButton, ToggleButtonGroup, Stack, Link, MenuItem, TextField, useTheme,
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, ReferenceLine,
} from 'recharts';
import { useQuery } from 'react-query';
import { Loading } from 'react-admin';
import { supabase } from '../../lib/supabaseClient';
import {
    SectionCard, Tag, EmptyState, Tile, ChecksTable, FunnelGroup, SplitsTable, PoolHealthBody,
} from '../tracking-pool/TrackingPoolPanels';
import {
    WindowDays, FunnelStep, PoolHealth, n0, num, formatMoney, formatDelta, posthogPersonUrl,
} from '../tracking-pool/format';
import { adsPoolPalette, AdsPoolPalette } from './adsPoolPalette';
import { limited, useSeen, useAsOf, daysBefore, RefreshButton, SectionLoading } from '../recapQuery';
import {
    AdsPoolSummary, AdsCoverageWeek, AdsArmRow, AdsSuspect, UploadHealth, BreakdownRow, AdsTrendWeek,
    LegacyLoad, Market, ADS_FUNNEL_LABEL, DIM_LABEL, lineLabel, EXPECTED_LINES, MARKET_LABEL, evaluateAdsChecks,
    languageFloor, mismatchByClass,
} from './adsPoolMetrics';

/**
 * Admin Analytics → Ads pool tab (plan docs/roadmap/ADS_POOL_REFRESH_PLAN.md §10–§11).
 *
 * Same pattern as the Organic tab: rolling window, react-query, one RPC per panel
 * (20260924200200_ads_pool_analytics.sql), recharts, and the shared panels in ../tracking-pool.
 * Paid pool numbers only — organic numbers are their own pool on the Organic tab.
 *
 * Day one (stages 0–1) every panel reads real data: the legacy pool (ad_tracking_logs + the two
 * legacy matchers), the stage-1 shadow ledger once it has run, and the T-019 nightly Google Ads
 * pulls once they are on. Panels that need the stage-2 canary (claims, blocking, the new pool's
 * matches) show "waiting", never a fake number.
 */

const WINDOWS: { key: WindowDays; label: string }[] = [
    { key: 7, label: '7 d' },
    { key: 28, label: '28 d' },
    { key: 90, label: '90 d' },
];
const SPLITS_DAYS = 90;          // pre-need revenue lands months later
const COVERAGE_MIN_DAYS = 84;    // enough weeks to show a direction
const TORONTO_NOTE = 'The Toronto Business Profile gets its own Ontario number (plan §7b; not bought yet). Until then — and for a few months after, while 805-6300 stays on the Toronto profile as an additional phone — Toronto profile calls land on 805-6300 (Google Maps · Montreal).';
const WAIT_STAGE2 = 'Waiting for the canary (stage 2)';
const WAIT_NIGHTLY = 'Waiting for the nightly Google Ads pull';

type Dim = 'campaign' | 'ad_group' | 'landing_page' | 'market';

export const AdsPoolRecap = ({ initialWindow = 28 }: { initialWindow?: WindowDays } = {}) => {
    const theme = useTheme();
    const p = adsPoolPalette(theme.palette.mode === 'dark' ? 'dark' : 'light');
    const [win, setWin] = useState<WindowDays>(initialWindow);
    const [campaign, setCampaign] = useState<string>('');
    const [dim, setDim] = useState<Dim>('campaign');
    const [market, setMarket] = useState<Market>('all');

    // Everything runs up to asOf; it is in every key so Refresh refetches what is on screen.
    const [asOf, refresh] = useAsOf('adsPool');
    const range = useMemo(() => ({
        start: daysBefore(asOf, win),
        coverageStart: daysBefore(asOf, Math.max(win, COVERAGE_MIN_DAYS)),
        splitsStart: daysBefore(asOf, SPLITS_DAYS),
        end: asOf,
    }), [win, asOf]);

    const rpc = <T,>(fn: string, args: Record<string, unknown>) => limited(async (): Promise<T[]> => {
        const { data, error } = await supabase.rpc(fn, args);
        if (error) throw error;
        return (data ?? []) as T[];
    });
    const rpcJson = <T,>(fn: string, args: Record<string, unknown>) => limited(async (): Promise<T | null> => {
        const { data, error } = await supabase.rpc(fn, args);
        if (error) throw error;
        return (data ?? null) as T | null;
    });
    const w = { p_start: range.start, p_end: range.end };

    // Lazy sections: a panel's RPCs run once it scrolls near the viewport (T-091). The headline
    // row (tiles, funnel, "Is it working?") always loads; its checks need suspects/upload/load/arms.
    const [lanesRef, lanesSeen] = useSeen();
    const [poolRef, poolSeen] = useSeen();
    const [breakdownRef, breakdownSeen] = useSeen();
    const [splitsRef, splitsSeen] = useSeen();

    const summary = useQuery(['adsPoolSummary', win, asOf], rpc<AdsPoolSummary>('get_ads_pool_summary', w));
    const funnel = useQuery(['adsPoolFunnel', win, campaign, asOf],
        rpc<FunnelStep>('get_ads_pool_funnel', { ...w, p_campaign: campaign || null }));
    const campaigns = useQuery(['adsPoolBreakdown', win, 'campaign', asOf],
        rpc<BreakdownRow>('get_ads_pool_breakdown', { ...w, p_dim: 'campaign' }));
    const suspects = useQuery(['adsPoolSuspects', win, asOf], rpc<AdsSuspect>('get_ads_pool_suspects', w));
    const upload = useQuery(['adsPoolUpload', win, asOf], rpcJson<UploadHealth>('get_ads_upload_health', w));
    const load = useQuery(['adsPoolLegacyLoad', win, asOf], rpcJson<LegacyLoad>('get_ads_pool_legacy_load', w));
    const arms = useQuery(['adsPoolArms', win, asOf], rpc<AdsArmRow>('get_ads_pool_arm_comparison', w));
    const coverage = useQuery(['adsPoolCoverage', win, asOf],
        rpc<AdsCoverageWeek>('get_ads_pool_coverage', { p_start: range.coverageStart, p_end: range.end }),
        { enabled: lanesSeen });
    const pool = useQuery(['adsPoolHealth', win, asOf], rpcJson<PoolHealth>('get_tracking_pool_health', { ...w, p_pool: 'paid' }),
        { enabled: poolSeen });
    const breakdown = useQuery(['adsPoolBreakdown', win, dim, asOf],
        rpc<BreakdownRow>('get_ads_pool_breakdown', { ...w, p_dim: dim }), { enabled: breakdownSeen });
    const trend = useQuery(['adsPoolTrend', asOf], rpc<AdsTrendWeek>('get_ads_pool_weekly_trend', { p_weeks: 26 }),
        { enabled: splitsSeen });
    const splitArgs = (lang: string, deal: string) =>
        ({ p_start: range.splitsStart, p_end: range.end, p_campaign: null, p_language: lang, p_deal_type: deal });
    const splitOpts = { enabled: splitsSeen };
    const enAt = useQuery(['adsPoolSplit', 'en', 'at_need', asOf], rpc<FunnelStep>('get_ads_pool_funnel', splitArgs('en', 'at_need')), splitOpts);
    const frAt = useQuery(['adsPoolSplit', 'fr', 'at_need', asOf], rpc<FunnelStep>('get_ads_pool_funnel', splitArgs('fr', 'at_need')), splitOpts);
    const enPre = useQuery(['adsPoolSplit', 'en', 'pre_need', asOf], rpc<FunnelStep>('get_ads_pool_funnel', splitArgs('en', 'pre_need')), splitOpts);
    const frPre = useQuery(['adsPoolSplit', 'fr', 'pre_need', asOf], rpc<FunnelStep>('get_ads_pool_funnel', splitArgs('fr', 'pre_need')), splitOpts);

    const s = summary.data?.[0];
    const checks = useMemo(
        () => evaluateAdsChecks({ summary: s, suspects: suspects.data, upload: upload.data ?? undefined, load: load.data ?? undefined, arms: arms.data }),
        [s, suspects.data, upload.data, load.data, arms.data]);

    if (summary.isLoading) return <Loading />;
    if (summary.error) {
        return (
            <Typography color="error">
                Could not load ads pool analytics: {String((summary.error as Error).message ?? summary.error)}
            </Typography>
        );
    }

    const tooltipStyle: CSSProperties = {
        background: theme.palette.background.paper,
        border: `1px solid ${p.grid}`, borderRadius: 6, fontSize: 13,
    };
    const linked = n0(s?.legacy_clean) + n0(s?.legacy_ambiguous) + n0(s?.legacy_after_hold);

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
                <ToggleButtonGroup size="small" exclusive value={win} onChange={(_, v) => v && setWin(v)}>
                    {WINDOWS.map(o => <ToggleButton key={o.key} value={o.key}>Last {o.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <TextField select size="small" label="Campaign (funnel)" value={campaign} sx={{ minWidth: 240 }}
                    onChange={e => setCampaign(e.target.value)}>
                    <MenuItem value="">All campaigns</MenuItem>
                    {(campaigns.data ?? []).filter(c => n0(c.sessions) + n0(c.clicks) > 0).map(c => (
                        <MenuItem key={c.dim_key} value={c.dim_key}>{c.dim_label}</MenuItem>
                    ))}
                </TextField>
                <RefreshButton asOf={asOf} onRefresh={refresh} />
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520 }}>
                    Paid pool numbers only (organic numbers are their own pool, on the Organic tab). Calls are classified
                    by the number dialled. Lead and revenue steps follow the clicks in the window, however long after.
                </Typography>
            </Stack>

            {/* ---- Headline tiles ---- */}
            <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Tile label="Paid sessions (with a gclid)" value={n0(s?.paid_sessions).toLocaleString('en-CA')}
                    detail={`${n0(s?.sessions_shown).toLocaleString('en-CA')} shown a pool number · ${formatDelta(s?.paid_sessions, s?.paid_sessions_prev)}`} />
                <Tile label="Calls to pool numbers" value={String(n0(s?.pool_calls))}
                    detail={`${n0(s?.pool_first_time_calls)} first-time · ${n0(s?.pool_linked_calls)} tied to a visit`} />
                <Tile label="Ambiguous matches" value={num(s?.ambiguous_pct) == null ? '—' : `${num(s?.ambiguous_pct)?.toFixed(0)} %`}
                    detail={`${n0(s?.legacy_ambiguous)} of ${linked} had 2+ candidate visits`} />
                <Tile label="Called after the hold" value={num(s?.after_hold_pct) == null ? '—' : `${num(s?.after_hold_pct)?.toFixed(0)} %`}
                    detail={`${n0(s?.legacy_after_hold)} matched to a number already re-issuable`} />
                <Tile label="gclid on pool-qualified" value={num(s?.gclid_attach_pct) == null ? '—' : `${num(s?.gclid_attach_pct)?.toFixed(0)} %`}
                    detail={`${n0(s?.pool_qualified_gclid)} of ${n0(s?.pool_qualified)} qualified leads`} />
                <Tile label="Won revenue (these clicks)" value={formatMoney(s?.cohort_revenue_cents)}
                    detail={`${n0(s?.cohort_won)} paid contracts · ${n0(s?.cohort_qualified)} qualified`} />
                <Tile label="Claims blocked (peak)" muted={!s?.pool_live}
                    value={s?.pool_live && num(s?.blocking_pct) != null ? `${num(s?.blocking_pct)?.toFixed(1)} %` : '—'}
                    detail={s?.pool_live ? `${n0(s?.claims_blocked)} of ${n0(s?.claims)} claims · ${n0(s?.claims_overflow)} overflowed` : WAIT_STAGE2} />
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, columnGap: 3 }}>
                <Box>
                    <AdsFunnelPanel steps={funnel.data} p={p} campaignLabel={campaign ? (campaigns.data ?? []).find(c => c.dim_key === campaign)?.dim_label : undefined} />
                </Box>
                <Box>
                    <SectionCard title="Is it working?" tags={<><Tag label="DAY ONE: BASELINE" kind="day1" /><Tag label="VERDICTS: STAGE 2" kind="stage2" /></>}
                        subtitle="Pass thresholds from plan §11. Until the canary runs, each row judges TODAY's legacy path — the baseline the cutover is measured against — so red is expected now.">
                        <ChecksTable checks={checks} p={p} nowLabel="Today" />
                    </SectionCard>
                </Box>
            </Box>

            {/* ---- Match quality + every ad line ---- */}
            <div ref={lanesRef}>
            <SectionLoading loading={coverage.isLoading} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, columnGap: 3 }}>
                <MatchQualityPanel weeks={coverage.data} p={p} tooltipStyle={tooltipStyle} days={Math.max(win, COVERAGE_MIN_DAYS)} newPool={!!s?.pool_live} />
                <LinesPanel upload={upload.data ?? null} win={win} />
            </Box>

            <AdLinesByWeekPanel weeks={coverage.data} p={p} tooltipStyle={tooltipStyle} days={Math.max(win, COVERAGE_MIN_DAYS)} />
            </div>

            {/* ---- Pool health ---- */}
            <div ref={poolRef}>
            <SectionLoading loading={pool.isLoading} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, columnGap: 3 }}>
                <LegacyLoadPanel load={load.data ?? null} market={market} setMarket={setMarket} p={p} tooltipStyle={tooltipStyle} win={win} />
                <MarketsPanel load={load.data ?? null} win={win} />
            </Box>
            <SectionCard title="New paid pool — blocked claims and numbers in use by hour (ET)" tags={<Tag label="NEEDS CANARY" kind="stage2" />}
                subtitle={pool.data?.pool_live ? 'Claims, blocked claims and numbers in use for the pool=\'paid\' numbers (get_tracking_pool_health).' : undefined}>
                <PoolFullTable load={load.data ?? null} live={!!pool.data?.pool_live} />
                {!pool.data?.pool_live ? (
                    <EmptyState title={WAIT_STAGE2}>
                        Once the canary numbers are active this shows claims and blocked claims by hour, numbers in use vs the
                        active pool size, inventory by status, and assign → call delay against the 30 + 120 minute window.
                    </EmptyState>
                ) : (
                    <PoolHealthBody pool={pool.data} p={p} tooltipStyle={tooltipStyle} />
                )}
            </SectionCard>
            </div>

            {/* ---- Legacy vs new + suspects ---- */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 3 }}>
                <ArmsPanel arms={arms.data} />
                <SuspectsPanel calls={suspects.data} p={p} tooltipStyle={tooltipStyle} />
            </Box>

            {/* ---- Uploads + breakdown ---- */}
            <div ref={breakdownRef}>
            <SectionLoading loading={breakdown.isLoading} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 3 }}>
                <UploadPanel upload={upload.data ?? null} summary={s} />
                <BreakdownPanel rows={breakdown.data} dim={dim} setDim={setDim} win={win} />
            </Box>
            </div>

            <div ref={splitsRef}>
            <SectionLoading loading={trend.isLoading || enAt.isLoading || frAt.isLoading || enPre.isLoading || frPre.isLoading} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, columnGap: 3 }}>
                <SectionCard title={`FR / EN × at-need / pre-need · last ${SPLITS_DAYS} days`} tags={<Tag label="DAY ONE" kind="day1" />}
                    subtitle="Leads from paid clicks, by lead language. Pre-need revenue lands 6–12 months later, so don't read it against at-need on this window.">
                    <SplitsTable revenueStep="won" rows={[
                        ['EN · at-need', enAt.data], ['FR · at-need', frAt.data],
                        ['EN · pre-need', enPre.data], ['FR · pre-need', frPre.data],
                    ]} />
                </SectionCard>
                <TrendPanel weeks={trend.data} p={p} tooltipStyle={tooltipStyle} />
            </Box>
            </div>
        </Box>
    );
};

// ───────────────────────────────────────────────────────────────────────────────

const AdsFunnelPanel = ({ steps, p, campaignLabel }: { steps: FunnelStep[] | undefined; p: AdsPoolPalette; campaignLabel?: string }) => {
    const rows = (steps ?? []).slice().sort((a, b) => a.step_order - b.step_order);
    const web = rows.filter(r => r.step_order <= 3);
    const phone = rows.filter(r => r.step_order >= 4);
    const won = rows.find(r => r.step === 'won');
    const clicks = rows.find(r => r.step === 'clicks');
    return (
        <SectionCard title={`Paid funnel${campaignLabel ? ` · ${campaignLabel}` : ''}`}
            tags={<><Tag label="DAY ONE" kind="day1" /><Tag label="CLICKS: NIGHTLY PULL" kind="nightly" /></>}
            subtitle="Website steps are thousands, phone steps tens: each group has its own scale. Calls are counted in the window; leads onward follow the window's clicks. Numbers in brackets are first-time callers.">
            <FunnelGroup title="On the website" list={web} labels={ADS_FUNNEL_LABEL} color={p.pool}
                unavailableText={step => (step === 'clicks' ? `${WAIT_NIGHTLY} (ads_daily)` : WAIT_STAGE2)} />
            <FunnelGroup title="On the phone" list={phone} labels={ADS_FUNNEL_LABEL} color={p.pool}
                unavailableText={() => WAIT_STAGE2} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Revenue: {formatMoney(won?.value_cents)} from {n0(won?.n)} paid contracts
                {clicks?.available && n0(clicks?.value_cents) > 0 ? ` · spend ${formatMoney(clicks?.value_cents)}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {clicks?.source ?? ''}. Sessions = first click per gclid (ad_attribution_journey). Matched = one candidate visit within the hold (legacy) or a new-pool match.
            </Typography>
        </SectionCard>
    );
};

const MatchQualityPanel = ({ weeks, p, tooltipStyle, days, newPool }: {
    weeks: AdsCoverageWeek[] | undefined; p: AdsPoolPalette; tooltipStyle: CSSProperties; days: number; newPool: boolean;
}) => (
    <SectionCard title="Match quality — calls to pool numbers by how they were matched"
        tags={<><Tag label="DAY ONE" kind="day1" />{!newPool && <Tag label="NEW-POOL SLICES: STAGE 2" kind="stage2" />}</>}
        subtitle="Orange and amber are calls matched by “latest row wins” — the ones that can hand Google someone else's gclid. After cutover orange should be ≈ 0 and amber becomes “unmatched” (lost, never wrong).">
        <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(weeks ?? []).map(r => ({
                week: r.week_start.slice(5) + (r.is_partial ? '*' : ''),
                clean: n0(r.pool_clean), ambiguous: n0(r.pool_ambiguous), after_hold: n0(r.pool_after_hold), none: n0(r.pool_none),
                new_matched: n0(r.pool_new_matched), new_unmatched: n0(r.pool_new_unmatched),
            }))} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid vertical={false} stroke={p.grid} />
                <XAxis dataKey="week" tick={{ fill: p.muted, fontSize: 11 }} axisLine={{ stroke: p.axis }} tickLine={false} />
                <YAxis tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="clean" name="Clean — one visit, within the hold" stackId="m" fill={p.clean} />
                <Bar dataKey="ambiguous" name="Ambiguous — 2+ visits" stackId="m" fill={p.ambiguous} />
                <Bar dataKey="after_hold" name="After the 40-min hold" stackId="m" fill={p.afterHold} />
                <Bar dataKey="none" name="No visit attached" stackId="m" fill={p.none} />
                {newPool && <Bar dataKey="new_matched" name="New pool — matched" stackId="m" fill={p.newMatched} />}
                {newPool && <Bar dataKey="new_unmatched" name="New pool — unmatched" stackId="m" fill={p.newUnmatched} radius={[4, 4, 0, 0]} />}
            </BarChart>
        </ResponsiveContainer>
        <Typography variant="caption" color="text.secondary">
            Last {days} days, weeks start Monday (ET); * = partial week. All calls to the pool numbers, first-time or not.
        </Typography>
    </SectionCard>
);

const AdLinesByWeekPanel = ({ weeks, p, tooltipStyle, days }: {
    weeks: AdsCoverageWeek[] | undefined; p: AdsPoolPalette; tooltipStyle: CSSProperties; days: number;
}) => {
    const rows = weeks ?? [];
    const split = rows.some(r => n0(r.gbp_montreal_ad) + n0(r.gbp_montreal_maps) + n0(r.gbp_toronto_ad) + n0(r.gbp_toronto_maps) > 0);
    const toronto = rows.some(r => n0(r.gbp_toronto_ad) + n0(r.gbp_toronto_maps) + n0(r.gbp_toronto_unsplit) > 0);
    // one series per configured default / pool-full line (site_phone_lines paid_* rows), labelled by paid_line_label()
    const configured = (rows[0]?.configured_lines ?? []).map(c => ({ line: c.line, label: c.label }));
    return (
        <SectionCard title="Every “ad” line, by week"
            tags={<><Tag label="DAY ONE" kind="day1" /><Tag label="MAPS SPLIT: NIGHTLY PULL" kind="nightly" /></>}
            subtitle={<>Calls by the number dialled. The Business Profile line is split per call from Google&apos;s call_view: <b>Maps ad (location asset)</b> stays paid; <b>Google Maps · Montreal / Toronto</b> is its own channel, not paid.</>}>
            <ResponsiveContainer width="100%" height={260}>
                <BarChart data={rows.map(r => ({
                    week: r.week_start.slice(5) + (r.is_partial ? '*' : ''),
                    pool: n0(r.pool_clean) + n0(r.pool_ambiguous) + n0(r.pool_after_hold) + n0(r.pool_none) + n0(r.pool_new_matched) + n0(r.pool_new_unmatched),
                    call_asset: n0(r.call_asset),
                    ...Object.fromEntries((r.configured_lines ?? []).map(c => [`cfg_${c.line}`, n0(c.calls)])),
                    maps_ad: n0(r.gbp_montreal_ad) + n0(r.gbp_toronto_ad),
                    maps_mtl: n0(r.gbp_montreal_maps), maps_tor: n0(r.gbp_toronto_maps),
                    unsplit: n0(r.gbp_montreal_unsplit) + n0(r.gbp_toronto_unsplit),
                }))} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                    <CartesianGrid vertical={false} stroke={p.grid} />
                    <XAxis dataKey="week" tick={{ fill: p.muted, fontSize: 11 }} axisLine={{ stroke: p.axis }} tickLine={false} />
                    <YAxis tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="pool" name="Pool numbers" stackId="l" fill={p.pool} />
                    <Bar dataKey="call_asset" name="805-7500 call asset" stackId="l" fill={p.callAsset} />
                    {configured.map((c, i) => (
                        <Bar key={c.line} dataKey={`cfg_${c.line}`} name={c.label} stackId="l" fill={p.configured[i % p.configured.length]} />
                    ))}
                    <Bar dataKey="maps_ad" name="Maps ad (location asset)" stackId="l" fill={p.mapsAd} />
                    <Bar dataKey="maps_mtl" name="Google Maps · Montreal" stackId="l" fill={p.mapsMontreal} />
                    {toronto && <Bar dataKey="maps_tor" name="Google Maps · Toronto" stackId="l" fill={p.mapsToronto} />}
                    <Bar dataKey="unsplit" name="805-6300 not split yet" stackId="l" fill={p.unsplit} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Last {days} days (ET weeks; * = partial). {split ? '' : `${WAIT_NIGHTLY}: until call_view is pulled, every 805-6300 call shows as “not split yet”. `}
                {TORONTO_NOTE} Annotate the date the Maps relabel ships: paid qualified leads drop ≈14 % that day by definition, not by performance.
            </Typography>
        </SectionCard>
    );
};

const LinesPanel = ({ upload, win }: { upload: UploadHealth | null; win: number }) => {
    const lines = upload?.lines ?? [];
    const get = (l: string) => lines.find(x => x.line === l);
    const gbp = get('gbp_montreal');
    const tor = get('gbp_toronto');
    const split = n0(gbp?.split_ad_calls) + n0(gbp?.split_maps_calls) > 0;
    const pctOf = (a: number, b: number) => (b > 0 ? `${Math.round((100 * a) / b)} %` : '—');
    const dim = <span style={{ opacity: 0.5 }}>n/a</span>;
    // default / pool-full numbers from site_phone_lines paid_* rows (labels from paid_line_label()); expected ones not bought yet shown as such
    const configured = lines.filter(l => ['default', 'pool_full'].includes(l.role));
    const configuredRows = [
        ...configured.map(row => ({ line: row.line, label: lineLabel(row), row })),
        ...EXPECTED_LINES.filter(e => !configured.some(c => c.line === e.line)).map(e => ({ line: e.line, label: e.label, row: undefined })),
    ];
    return (
        <SectionCard title="Every “ad” line, not just the pool" tags={<Tag label="DAY ONE" kind="day1" />}
            subtitle={`Last ${win} days. Qualified = leads whose first inbound call was on that line, qualified in the window.`}>
            <Table size="small">
                <TableHead><TableRow>
                    <TableCell>Line</TableCell><TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Qual.</TableCell><TableCell align="right">gclid</TableCell>
                </TableRow></TableHead>
                <TableBody>
                    {(['pool', 'call_asset'] as const).map(l => {
                        const r = get(l);
                        return (
                            <TableRow key={l}>
                                <TableCell>{r ? lineLabel(r) : l}
                                    {l === 'call_asset' && n0(r?.calls) > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            campaign from call_view: {n0(r?.campaign_matched_calls) > 0 ? pctOf(n0(r?.campaign_matched_calls), n0(r?.calls)) : WAIT_NIGHTLY.toLowerCase()}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">{n0(r?.calls)}</TableCell>
                                <TableCell align="right">{n0(r?.qualified)}</TableCell>
                                <TableCell align="right">{l === 'pool' ? pctOf(n0(r?.qualified_gclid), n0(r?.qualified)) : dim}</TableCell>
                            </TableRow>
                        );
                    })}
                    <TableRow>
                        <TableCell>805-6300 · Maps ad (location asset)
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>stays paid, matched per call</Typography>
                        </TableCell>
                        <TableCell align="right">{split ? n0(gbp?.split_ad_calls) : '—'}</TableCell>
                        <TableCell align="right" rowSpan={2}>{n0(gbp?.qualified)}</TableCell>
                        <TableCell align="right" rowSpan={2}>{dim}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Google Maps · Montreal
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {split ? 'own channel, leaves the paid column' : `${n0(gbp?.calls)} calls on 805-6300, not split yet — ${WAIT_NIGHTLY.toLowerCase()}`}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">{split ? n0(gbp?.split_maps_calls) : '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Google Maps · Toronto
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {n0(tor?.calls) > 0 ? 'Toronto profile number' : 'number not bought yet — Toronto profile calls still land on 805-6300'}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">{n0(tor?.calls) > 0 ? n0(tor?.calls) : '—'}</TableCell>
                        <TableCell align="right">{n0(tor?.calls) > 0 ? n0(tor?.qualified) : '—'}</TableCell>
                        <TableCell align="right">{dim}</TableCell>
                    </TableRow>
                    {configuredRows.map(r => (
                        <TableRow key={r.line}>
                            <TableCell>{r.label}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {r.row ? `${r.row.role === 'default' ? 'shown before the swap' : 'shown only when both paid sub-pools are full'} · no gclid (shared number)`
                                        : 'not bought yet'}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">{r.row ? n0(r.row.calls) : '—'}</TableCell>
                            <TableCell align="right">{r.row ? n0(r.row.qualified) : '—'}</TableCell>
                            <TableCell align="right">{dim}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Calls with a call_sessions row. 805-7500 and the Maps lines can never carry a gclid (Google forwarding numbers); they upload
                hashed contact details only. The Maps qualified count covers both halves until leads are split per channel.
            </Typography>
        </SectionCard>
    );
};

const LegacyLoadPanel = ({ load, market, setMarket, p, tooltipStyle, win }: {
    load: LegacyLoad | null; market: Market; setMarket: (m: Market) => void; p: AdsPoolPalette; tooltipStyle: CSSProperties; win: number;
}) => {
    const rows = (load?.hourly ?? []).filter(h => h.market === market);
    const m = load?.markets.find(x => x.market === market);
    const proposed = n0(m?.pool_size_proposed);
    const now = n0(m?.pool_size_now);
    return (
        <SectionCard title="Pool health — numbers held by hour (ET)"
            tags={<><Tag label="DAY ONE: LEGACY POOL" kind="day1" /><Tag label="CLAIMS / BLOCKING: STAGE 2" kind="stage2" /></>}
            subtitle={`Last ${win} days. Peak numbers held under today's 40-minute rule vs the new rule simulated on the same visitors (one number each, held for visible time + 30 min hold + 120 min cooldown), against today's pool and the proposed ${market === 'all' ? '42 (28 QC + 14 ON)' : `${MARKET_LABEL[market]} sub-pool`}.`}>
            <ToggleButtonGroup size="small" exclusive value={market} onChange={(_, v) => v && setMarket(v)} sx={{ mb: 1 }}>
                {(['all', 'qc', 'on'] as Market[]).map(k => <ToggleButton key={k} value={k}>{MARKET_LABEL[k]}</ToggleButton>)}
            </ToggleButtonGroup>
            {!load ? <EmptyState title="Loading…" /> : (
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={rows.map(h => ({ hour: `${h.hour_et}h`, legacy: n0(h.max_in_use_legacy), sim: n0(h.max_in_use_150), arrivals: n0(h.arrivals_per_hour) }))}>
                        <CartesianGrid vertical={false} stroke={p.grid} />
                        <XAxis dataKey="hour" tick={{ fill: p.muted, fontSize: 11 }} interval={2} axisLine={{ stroke: p.axis }} tickLine={false} />
                        <YAxis tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {now > 0 && <ReferenceLine y={now} stroke={p.axis} strokeDasharray="4 3"
                            label={{ value: `today ${now}`, fill: p.muted, fontSize: 11, position: 'insideTopLeft' }} />}
                        <ReferenceLine y={proposed} stroke={p.critical} strokeDasharray="5 4"
                            label={{ value: `proposed ${proposed}`, fill: p.critical, fontSize: 11, position: 'insideTopRight' }} />
                        <Bar dataKey="legacy" name="Peak held · today's 40-min rule" fill={p.legacy} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="sim" name="Peak held · 150-min rule (simulated)" fill={p.sim150} radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            )}
            {load && (
                <Table size="small" sx={{ mt: 1 }}>
                    <TableBody>
                        <TableRow><TableCell>Visitors given a number, per day</TableCell><TableCell align="right">{Math.round(rows.reduce((a, h) => a + n0(h.arrivals_per_hour), 0))}</TableCell></TableRow>
                        <TableRow><TableCell>Assign → call p50 / p75 / p90 / p95</TableCell><TableCell align="right">{[load.stats.delay_p50_min, load.stats.delay_p75_min, load.stats.delay_p90_min, load.stats.delay_p95_min].map(v => num(v)?.toFixed(0) ?? '—').join(' / ')} min</TableCell></TableRow>
                        <TableRow><TableCell>Sessions given 2+ numbers</TableCell><TableCell align="right">{n0(load.stats.sessions_two_numbers)} of {n0(load.stats.sessions_shown)}</TableCell></TableRow>
                        <TableRow><TableCell>Assigned a pool-full number (legacy exhaustion)</TableCell><TableCell align="right">{n0(load.stats.fallback_assignments)}</TableCell></TableRow>
                        <TableRow><TableCell>Legacy numbers · hold</TableCell><TableCell align="right">{n0(load.stats.legacy_numbers)} local 438 · {n0(load.stats.hold_minutes)} min from assignment</TableCell></TableRow>
                    </TableBody>
                </Table>
            )}
        </SectionCard>
    );
};

const PoolFullTable = ({ load, live }: { load: LegacyLoad | null; live: boolean }) => {
    const rows = load?.pool_full ?? [];
    const pc = (v: unknown) => (num(v) == null ? '—' : `${(num(v) as number).toFixed(1)} %`);
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Blocked claims by market</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                A full market first overflows to the other market&apos;s numbers; only when both paid sub-pools are full does the visitor get
                their market&apos;s pool-full number (plan Q16). Claims are counted by the market the visitor asked for.
            </Typography>
            <Table size="small">
                <TableHead><TableRow>
                    <TableCell>Market</TableCell><TableCell>Pool-full number</TableCell>
                    <TableCell align="right">Claims</TableCell><TableCell align="right">Overflowed</TableCell>
                    <TableCell align="right">Blocked</TableCell><TableCell align="right">Calls to pool-full</TableCell>
                </TableRow></TableHead>
                <TableBody>
                    {rows.map(r => (
                        <TableRow key={r.market}>
                            <TableCell>{MARKET_LABEL[r.market]}</TableCell>
                            <TableCell>{r.label ? `${r.label}${r.display ? ` · ${r.display}` : ''}` : <span style={{ opacity: 0.6 }}>{`paid · ${r.market.toUpperCase()} pool-full — not bought yet`}</span>}</TableCell>
                            <TableCell align="right">{live ? n0(r.claims) : '—'}</TableCell>
                            <TableCell align="right">{live ? `${n0(r.overflow)} · ${pc(r.overflow_pct)}` : '—'}</TableCell>
                            <TableCell align="right">{live ? `${n0(r.blocked)} · ${pc(r.blocking_pct)}` : '—'}</TableCell>
                            <TableCell align="right">{r.e164 ? n0(r.pool_full_calls) : '—'}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {!live && (
                <Typography variant="caption" color="text.secondary">
                    Claims start with the canary (stage 2). Calls to the pool-full number today are the legacy exhaustion fallback.
                </Typography>
            )}
        </Box>
    );
};

const MarketsPanel = ({ load, win }: { load: LegacyLoad | null; win: number }) => {
    const on = load?.markets.find(m => m.market === 'on');
    const qc = load?.markets.find(m => m.market === 'qc');
    const row = (label: string, a: string, b: string) => (
        <TableRow><TableCell>{label}</TableCell><TableCell align="right">{a}</TableCell><TableCell align="right">{b}</TableCell></TableRow>
    );
    const f = (v: unknown, d = 1) => (num(v) == null ? '—' : (num(v) as number).toFixed(d));
    const pctOf = (a: unknown, b: unknown) => (n0(b) > 0 ? `${Math.round((100 * n0(a)) / n0(b))} %` : '—');
    return (
        <SectionCard title="Ontario vs Quebec pages" tags={<Tag label="DAY ONE" kind="day1" />}
            subtitle={`Last ${win} days, paid sessions. Market = the page's own province (plan §6a). Today both markets see a 438 number: this is the baseline the Ontario numbers are judged against (±15 %).`}>
            <Table size="small">
                <TableHead><TableRow><TableCell /><TableCell align="right">Ontario pages</TableCell><TableCell align="right">Quebec / other</TableCell></TableRow></TableHead>
                <TableBody>
                    {row('Paid sessions', n0(on?.sessions).toLocaleString('en-CA'), n0(qc?.sessions).toLocaleString('en-CA'))}
                    {row('Shown a pool number', n0(on?.sessions_shown).toLocaleString('en-CA'), n0(qc?.sessions_shown).toLocaleString('en-CA'))}
                    {row('Calls per 100 shown', f(on?.calls_per_100_shown), f(qc?.calls_per_100_shown))}
                    {row('Qualified per 100 shown', f(on?.qualified_per_100_shown), f(qc?.qualified_per_100_shown))}
                    {row('Callers with an Ontario area code', pctOf(on?.ontario_area_callers, on?.callers), pctOf(qc?.ontario_area_callers, qc?.callers))}
                    {row('Proposed sub-pool (150-min window)', String(n0(on?.pool_size_proposed)), String(n0(qc?.pool_size_proposed)))}
                </TableBody>
            </Table>
        </SectionCard>
    );
};

const ARM_LABEL: Record<string, string> = { legacy: 'Legacy (today)', shadow: 'Shadow ledger (new rule, replayed)', canary: 'New pool (canary)' };

const ArmsPanel = ({ arms }: { arms: AdsArmRow[] | undefined }) => {
    const list = arms ?? [];
    const legacy = list.find(a => a.arm === 'legacy');
    const shadow = list.find(a => a.arm === 'shadow');
    const canary = list.find(a => a.arm === 'canary');
    const pc = (v: unknown) => (num(v) == null ? '—' : `${(num(v) as number).toFixed(0)} %`);
    const cell = (a: AdsArmRow | undefined, v: (a: AdsArmRow) => string) => (a?.available ? v(a) : '—');
    const rows: [string, (a: AdsArmRow) => string, string][] = [
        ['Pool calls', a => String(n0(a.calls)), ''],
        ['Matched to exactly one visit', a => pc(a.matched_pct), 'ambiguous ≈ 0'],
        ['Ambiguous (2+ candidates)', a => pc(a.ambiguous_pct), '≤ 1 %'],
        ['Unmatched / no visit', a => pc(a.unmatched_pct), '≤ 8 %'],
        ['After the 40-min hold (guessed)', a => (a.arm === 'legacy' ? String(n0(a.after_hold)) : '0 by design'), '0 guessed'],
        ['gclid on qualified', a => `${pc(a.gclid_attach_pct)} · ${n0(a.qualified_gclid)}/${n0(a.qualified)}`, '≥ legacy − 3 pts'],
        ['Language mismatch (matched)', a => `${pc(a.mismatch_pct)} of ${n0(a.language_known)}`, 'floor + 3 pts'],
    ];
    return (
        <SectionCard title="Legacy vs new rule (shadow now, canary later)"
            tags={<><Tag label="SHADOW: STAGE 1" kind="p1" /><Tag label="CANARY: STAGE 2" kind="stage2" /></>}
            subtitle="Same calls, three matchers. The shadow replays today's legacy assignments under the 150-min window (paid_shadow_call_matches). The canary column is the cutover gate: 3 weeks and ≥ 30 canary calls, every row passing.">
            {!shadow?.available && !canary?.available && (
                <Box sx={{ mb: 1.5 }}>
                    <EmptyState title="Shadow ledger has not run for this window yet">
                        It fills nightly once ADS_PAID_SHADOW_ENABLED is on (rebuild_paid_shadow_ledger over the last 3 days).
                    </EmptyState>
                </Box>
            )}
            <TableContainer>
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Measure</TableCell>
                        <TableCell align="right">{ARM_LABEL.legacy}</TableCell>
                        <TableCell align="right">{ARM_LABEL.shadow}</TableCell>
                        <TableCell align="right">{ARM_LABEL.canary}</TableCell>
                        <TableCell>Gate</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {rows.map(([label, v, gate]) => (
                            <TableRow key={label}>
                                <TableCell>{label}</TableCell>
                                <TableCell align="right">{cell(legacy, v)}</TableCell>
                                <TableCell align="right">{cell(shadow, v)}</TableCell>
                                <TableCell align="right">{canary?.available ? v(canary) : <span style={{ opacity: 0.6 }}>stage 2</span>}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{gate}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {shadow?.available && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Shadow agrees with legacy matcher #2 on {n0(shadow.agrees_with_legacy)} calls; {n0(shadow.different_gclid)} would carry a different gclid.
                    The replay inherits today&apos;s re-issues (the 40-min rule hands a number out again inside the new window), so shadow &quot;ambiguous&quot;
                    counts overlaps the new pool will not create.
                </Typography>
            )}
        </SectionCard>
    );
};

const SuspectsPanel = ({ calls, p, tooltipStyle }: { calls: AdsSuspect[] | undefined; p: AdsPoolPalette; tooltipStyle: CSSProperties }) => {
    const list = calls ?? [];
    const floor = languageFloor(list);
    const byClass = mismatchByClass(list);
    const flagged = list.filter(c => c.is_suspect);
    return (
        <SectionCard title="Cross-attribution suspects" tags={<Tag label="DAY ONE" kind="day1" />}
            subtitle="Share of matched calls where the lead's language differs from the landing page / campaign. Clean matches set the floor (bilingual families are normal); anything above it is a wrong-session signal. Replaces nulling gclids by hand.">
            {list.length === 0 ? (
                <EmptyState title="No matched pool calls in this window." />
            ) : (
                <>
                    <ResponsiveContainer width="100%" height={40 + byClass.length * 36}>
                        <BarChart layout="vertical" data={byClass.map(r => ({ label: r.label, rate: r.rate == null ? 0 : Math.round(r.rate), known: r.known, cls: r.cls }))}
                            margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
                            <CartesianGrid horizontal={false} stroke={p.grid} />
                            <XAxis type="number" unit=" %" domain={[0, 40]} tick={{ fill: p.muted, fontSize: 11 }} axisLine={{ stroke: p.axis }} tickLine={false} />
                            <YAxis type="category" dataKey="label" width={210} tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, item) => [`${String(v)} % of ${String((item as { payload?: { known?: number } }).payload?.known ?? '?')} calls`, 'Language mismatch']} />
                            {floor.floor != null && <ReferenceLine x={floor.floor} stroke={p.muted} strokeDasharray="4 3"
                                label={{ value: `floor ${floor.floor.toFixed(0)} %`, fill: p.muted, fontSize: 11, position: 'top' }} />}
                            <Bar dataKey="rate" fill={p.ambiguous} radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {flagged.length} of {list.length} matched calls flagged (language mismatch, 2+ candidates, after the hold, or a caller outside Quebec/Ontario).
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                            <TableHead><TableRow>
                                <TableCell>Call</TableCell><TableCell>Page (lang)</TableCell><TableCell>Lead lang</TableCell>
                                <TableCell>Flag</TableCell><TableCell>Links</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {flagged.slice(0, 50).map(c => {
                                    const ph = posthogPersonUrl(c.posthog_distinct_id);
                                    return (
                                        <TableRow key={c.twilio_call_sid} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(c.called_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                                            <TableCell>{c.landing_page ?? '—'} ({c.implied_language ?? '?'})</TableCell>
                                            <TableCell>{c.lead_language ?? '—'}</TableCell>
                                            <TableCell>{c.reasons.join(', ').replace(/_/g, ' ')}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {c.lead_id && <Link href={`#/leads/${c.lead_id}/show`} sx={{ mr: 1 }}>Lead</Link>}
                                                {ph && <Link href={ph} target="_blank" rel="noopener noreferrer">PostHog</Link>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </SectionCard>
    );
};

const UploadPanel = ({ upload, summary }: { upload: UploadHealth | null; summary: AdsPoolSummary | undefined }) => {
    const lines = (upload?.lines ?? []).filter(l => n0(l.qualified) > 0 || l.line === 'pool');
    return (
        <SectionCard title="gclid attach & offline conversion upload"
            tags={<><Tag label="OURS: DAY ONE" kind="day1" /><Tag label="GOOGLE STATUS: NIGHTLY PULL" kind="nightly" /></>}
            subtitle="What Google receives for every qualified lead. Watch the pool row — it must not drop at cutover (alert below 85 % for 7 days; baseline 91 %).">
            <Table size="small">
                <TableHead><TableRow>
                    <TableCell>Qualified leads from</TableCell><TableCell align="right">Qual.</TableCell><TableCell align="right">With gclid</TableCell>
                    <TableCell align="right">Hashed contact only</TableCell><TableCell align="right">Sent / failed / pending</TableCell>
                </TableRow></TableHead>
                <TableBody>
                    {lines.map(l => (
                        <TableRow key={l.line}>
                            <TableCell>{lineLabel(l)}</TableCell>
                            <TableCell align="right">{n0(l.qualified)}</TableCell>
                            <TableCell align="right">{n0(l.qualified_gclid)}{n0(l.qualified) > 0 ? ` · ${Math.round((100 * n0(l.qualified_gclid)) / n0(l.qualified))} %` : ''}</TableCell>
                            <TableCell align="right">{n0(l.qualified_ecl_only)}</TableCell>
                            <TableCell align="right">{n0(l.sent)} / {n0(l.failed)} / {n0(l.pending)}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                        <TableCell>All paid leads</TableCell><TableCell align="right">{n0(summary?.uploads_qualified)}</TableCell>
                        <TableCell /><TableCell />
                        <TableCell align="right">{n0(summary?.uploads_sent)} / {n0(summary?.uploads_failed)} / {n0(summary?.uploads_pending)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>Google-side status</Typography>
            {!upload?.google_available ? (
                <EmptyState title={WAIT_NIGHTLY}>
                    offline_conversion_upload_conversion_action_summary lands in ads_upload_health_daily once ADS_NIGHTLY_PULLS_ENABLED is on.
                </EmptyState>
            ) : (
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Conversion action</TableCell><TableCell>Status</TableCell>
                        <TableCell align="right">OK / total</TableCell><TableCell align="right">Failed</TableCell><TableCell align="right">Last upload</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {upload.google.map(g => (
                            <TableRow key={`${g.conversion_action_id}-${g.client}`}>
                                <TableCell>{g.conversion_action_name ?? g.conversion_action_id}</TableCell>
                                <TableCell>{g.status ?? '—'}{(g.alerts ?? []).length ? ` · ${(g.alerts ?? []).length} alert(s)` : ''}</TableCell>
                                <TableCell align="right">{n0(g.successful_event_count)} / {n0(g.total_event_count)}</TableCell>
                                <TableCell align="right">{n0(g.failed_event_count)}</TableCell>
                                <TableCell align="right">{g.last_upload_at ? new Date(g.last_upload_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {upload?.google_available && (
                <Typography variant="caption" color="text.secondary">Snapshot {upload.latest_snapshot}.</Typography>
            )}
        </SectionCard>
    );
};

const BreakdownPanel = ({ rows, dim, setDim, win }: { rows: BreakdownRow[] | undefined; dim: Dim; setDim: (d: Dim) => void; win: number }) => {
    const list = (rows ?? []).filter(r => n0(r.sessions) + n0(r.calls) + n0(r.clicks) > 0);
    const hasCost = list.some(r => r.cost_cents != null);
    const maxS = Math.max(1, ...list.map(r => n0(r.sessions)));
    return (
        <SectionCard title={`By ${DIM_LABEL[dim].toLowerCase()} · last ${win} days`}
            tags={<><Tag label="DAY ONE" kind="day1" />{dim === 'campaign' && <Tag label="COST: NIGHTLY PULL" kind="nightly" />}</>}
            subtitle="Sessions → revenue follow the window's clicks; calls are pool calls in the window, by their click. Ad groups come from gclid_ads_metadata (click_view enrichment).">
            <ToggleButtonGroup size="small" exclusive value={dim} onChange={(_, v) => v && setDim(v)} sx={{ mb: 1 }}>
                {(['campaign', 'ad_group', 'landing_page', 'market'] as Dim[]).map(d => <ToggleButton key={d} value={d}>{DIM_LABEL[d]}</ToggleButton>)}
            </ToggleButtonGroup>
            <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                        <TableCell>{DIM_LABEL[dim]}</TableCell>
                        <TableCell align="right">Sessions</TableCell><TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Qual.</TableCell><TableCell align="right">Won</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        {hasCost && <TableCell align="right">Cost / qual.</TableCell>}
                    </TableRow></TableHead>
                    <TableBody>
                        {list.slice(0, 30).map(r => (
                            <TableRow key={r.dim_key}>
                                <TableCell sx={{ maxWidth: 280 }}>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <Box sx={{ width: Math.max(2, (60 * n0(r.sessions)) / maxS), height: 10, bgcolor: 'primary.main', borderRadius: '0 4px 4px 0', opacity: 0.8, flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.dim_label}>{r.dim_label}</span>
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">{n0(r.sessions).toLocaleString('en-CA')}</TableCell>
                                <TableCell align="right">{n0(r.calls)}</TableCell>
                                <TableCell align="right">{n0(r.qualified)}</TableCell>
                                <TableCell align="right">{n0(r.won)}</TableCell>
                                <TableCell align="right">{formatMoney(r.revenue_cents)}</TableCell>
                                {hasCost && <TableCell align="right">{r.cost_per_qualified_cents == null ? '—' : formatMoney(r.cost_per_qualified_cents)}</TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {dim === 'campaign' && !hasCost && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Cost per qualified appears once ads_daily is pulled nightly.
                </Typography>
            )}
        </SectionCard>
    );
};

const TrendPanel = ({ weeks, p, tooltipStyle }: { weeks: AdsTrendWeek[] | undefined; p: AdsPoolPalette; tooltipStyle: CSSProperties }) => {
    const rows = weeks ?? [];
    const shadow = rows.some(r => n0(r.shadow_calls) > 0);
    return (
        <SectionCard title="Weekly trend · 26 weeks" tags={<Tag label="DAY ONE" kind="day1" />}
            subtitle="Share of pool calls matched cleanly, share ambiguous, and gclid attach on pool-qualified leads. The last week is partial. After cutover the clean line should sit near the top and ambiguous near zero.">
            <ResponsiveContainer width="100%" height={260}>
                <LineChart data={rows.map(r => ({
                    week: r.week_start.slice(5) + (r.is_partial ? '*' : ''),
                    clean: num(r.clean_pct), ambiguous: num(r.ambiguous_pct), attach: num(r.gclid_attach_pct), shadow: num(r.shadow_matched_pct),
                }))} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid vertical={false} stroke={p.grid} />
                    <XAxis dataKey="week" tick={{ fill: p.muted, fontSize: 11 }} axisLine={{ stroke: p.axis }} tickLine={false} interval={3} />
                    <YAxis unit=" %" domain={[0, 100]} tick={{ fill: p.muted, fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine y={88} stroke={p.axis} strokeDasharray="4 3" label={{ value: 'attach ≥ 88 %', fill: p.muted, fontSize: 11, position: 'insideBottomRight' }} />
                    <Line type="monotone" dataKey="clean" name="Clean-match share" stroke={p.clean} strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="ambiguous" name="Ambiguous share" stroke={p.ambiguous} strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="attach" name="gclid attach (pool-qualified)" stroke={p.callAsset} strokeWidth={2} dot={false} connectNulls />
                    {shadow && <Line type="monotone" dataKey="shadow" name="Shadow matched share" stroke={p.afterHold} strokeDasharray="5 3" strokeWidth={2} dot={false} connectNulls />}
                </LineChart>
            </ResponsiveContainer>
            <Typography variant="caption" color="text.secondary">
                Before mid-May most pool calls had no visit attached (the matcher wrote no session link), so their clean share reads near zero.
            </Typography>
        </SectionCard>
    );
};
